import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { cancelAIGeneration } from "../api";
import { useNotification } from "../context/NotificationContext";
import { CancelAIGenerationResponse } from "../types/requestsOutputs";

/**
 * Cancels the pending text, remix or TTS generation of an AudioAI.
 *
 * The cancel request only flips the queue status: the page's own status polling then sees
 * `canceled` and closes the status modal, calling `confirmCanceled`. Leaving the modal open
 * until then keeps the user from relaunching while the old polling loop is still running.
 * If the generation finished in the meantime, the polling simply shows its result.
 *
 * Text-to-Speech is the exception: its canceled AudioAIs are deleted by the cancel request, so
 * there is nothing left to poll. `onCanceled` then lets the page conclude right away, as soon
 * as the request has canceled something.
 */
export default function useCancelGeneration() {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const [cancelling, setCancelling] = useState(false);

    const cancelGeneration = useCallback(
        async (audioAIId: string | null | undefined, onCanceled?: (result: CancelAIGenerationResponse) => void) => {
            const token = localStorage.getItem("token");
            if (!token || !audioAIId) return;

            setCancelling(true);
            try {
                const result = await cancelAIGeneration({ token, audioAIId });
                // Nothing canceled: already finished (the polling shows it) or launched by someone else.
                if (!result?.canceled) {
                    setCancelling(false);
                } else if (onCanceled) {
                    setCancelling(false);
                    showNotification({ message: t("generationCanceled"), type: "info" });
                    onCanceled(result);
                }
            } catch (error) {
                console.error("Error canceling generation:", error);
                setCancelling(false);
                showNotification({ message: t("somethingWentWrong"), type: "error" });
            }
        },
        [showNotification, t],
    );

    /** To call from the polling loop once it reads `canceled`. */
    const confirmCanceled = useCallback(() => {
        setCancelling(false);
        showNotification({ message: t("generationCanceled"), type: "info" });
    }, [showNotification, t]);

    /** To call when the polling loop ends any other way (success or failure). */
    const resetCancelling = useCallback(() => setCancelling(false), []);

    return { cancelling, cancelGeneration, confirmCanceled, resetCancelling };
}
